const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const normalizeMonthlyValues = (monthly, fallback = 0) => {
  const values = Array.isArray(monthly)
    ? monthly.map((val) => toNumber(val, 0))
    : [];
  if (values.length >= 12) return values.slice(0, 12);
  return [...values, ...Array(12 - values.length).fill(fallback)].slice(0, 12);
};

const sumValues = (values) =>
  Array.isArray(values)
    ? values.reduce((sum, val) => sum + toNumber(val, 0), 0)
    : toNumber(values, 0);

const isBinaryEntry = (value) =>
  value === 0 || value === 1 || value === true || value === false;

const toPanelCount = (value) =>
  Math.max(0, Math.trunc(toNumber(value, 0)));

const isValidPanelIndex = (value, total) =>
  Number.isInteger(value) && value >= 0 && value < total;

const countSelectedPanels = (selectedPanelsPerRoof = {}) =>
  Object.values(selectedPanelsPerRoof || {}).reduce(
    (sum, list) =>
      sum + (Array.isArray(list) ? list.filter(Number.isFinite).length : 0),
    0
  );

const buildPitchAndAzimuthPerRoof = (segments = []) => {
  if (!Array.isArray(segments)) return [];
  return segments.map((segment, idx) => ({
    idx,
    azimuth: segment?.azimuth,
    pitch: segment?.pitch,
  }));
};

const buildShadingPerRoof = (segments = []) => {
  if (!Array.isArray(segments)) return [];
  return segments.map((segment, idx) => ({
    idx,
    SA:
      segment?.annualAccess ??
      segment?.solarAccess?.[12] ??
      segment?.solar_access?.[12] ??
      0,
    TSRF: segment?.annualTSRF ?? segment?.tsrf?.[12] ?? 0,
    TOF: segment?.annualTOF ?? segment?.tof?.[12] ?? 0,
  }));
};

const getRoofPanelCountFromSegment = (segment) => {
  if (!segment || typeof segment !== 'object') return 0;
  if (Array.isArray(segment?.panels)) return segment.panels.length;

  const horizontal = toNumber(segment?.horizontal, 0);
  const vertical = toNumber(segment?.vertical, 0);
  const directCount = horizontal + vertical;
  if (directCount > 0) return directCount;

  const horizontalPoints = Array.isArray(segment?.horizontal?.points)
    ? segment.horizontal.points.length
    : 0;
  const verticalPoints = Array.isArray(segment?.vertical?.points)
    ? segment.vertical.points.length
    : 0;
  return horizontalPoints + verticalPoints;
};

const buildFallbackProductionPerPanel = ({
  segments,
  perRoofCounts,
  selectedPanelsPerRoof,
  selectedPanelCount,
  annualKwhValue,
}) => {
  const safeSegments = Array.isArray(segments) ? segments : [];
  const safePerRoofCounts = Array.isArray(perRoofCounts) ? perRoofCounts : [];
  const selectedByRoof =
    selectedPanelsPerRoof && typeof selectedPanelsPerRoof === 'object'
      ? selectedPanelsPerRoof
      : {};
  const selectedRoofIndices = Object.keys(selectedByRoof)
    .map((key) => Number(key))
    .filter((idx) => Number.isFinite(idx) && idx >= 0);

  const roofCount = Math.max(
    safeSegments.length,
    safePerRoofCounts.length,
    selectedRoofIndices.length > 0 ? Math.max(...selectedRoofIndices) + 1 : 0
  );
  if (roofCount <= 0) return [];

  const resolvedSelectedPanelCount = Number.isFinite(Number(selectedPanelCount))
    ? Number(selectedPanelCount)
    : countSelectedPanels(selectedByRoof);
  const annualPerSelectedPanel =
    resolvedSelectedPanelCount > 0
      ? annualKwhValue / resolvedSelectedPanelCount
      : 0;

  return Array.from({ length: roofCount }, (_, roofIdx) => {
    const selectedRaw =
      selectedByRoof?.[roofIdx] ?? selectedByRoof?.[String(roofIdx)] ?? [];
    const selectedIndices = Array.isArray(selectedRaw)
      ? selectedRaw
          .map((panelIdx) => Number(panelIdx))
          .filter((panelIdx) => Number.isFinite(panelIdx) && panelIdx >= 0)
      : [];
    const uniqueSelected = [...new Set(selectedIndices)];

    const countFromPayload = Number(safePerRoofCounts?.[roofIdx]);
    const countFromSegment = getRoofPanelCountFromSegment(safeSegments?.[roofIdx]);
    const countFromSelected =
      uniqueSelected.length > 0 ? Math.max(...uniqueSelected) + 1 : 0;
    const roofPanelCount = Math.max(
      Number.isFinite(countFromPayload) ? countFromPayload : 0,
      countFromSegment,
      countFromSelected
    );

    const roofPanels = Array(Math.max(roofPanelCount, 0)).fill(0);
    uniqueSelected.forEach((panelIdx) => {
      if (panelIdx >= 0 && panelIdx < roofPanels.length) {
        roofPanels[panelIdx] = annualPerSelectedPanel;
      }
    });

    return {
      idx: roofIdx,
      roofPanels,
    };
  });
};

export const getPanelCounts = ({
  panelData,
  panels,
  design,
  designPanels,
} = {}) => {
  if (Array.isArray(panelData) && panelData.length > 0) {
    const perRoofCounts = panelData.map((panelEntry) => {
      try {
        const parsed =
          typeof panelEntry === 'string' ? JSON.parse(panelEntry) : panelEntry;
        const realPanels = Array.isArray(parsed?.realPanels)
          ? parsed.realPanels
          : null;
        if (realPanels) return realPanels.filter(Boolean).length;
        const points3D = Array.isArray(parsed?.points3D)
          ? parsed.points3D
          : [];
        return points3D.length;
      } catch (e) {
        return 0;
      }
    });
    const totalCount = perRoofCounts.reduce((sum, count) => sum + count, 0);
    return { totalCount, perRoofCounts };
  }

  if (Array.isArray(panels) && panels.length > 0) {
    const perRoofCounts = panels.map((panel) => {
      const horizontalCount = panel?.horizontal?.points?.length ??
        panel?.horizontal?.length ?? 0;
      const verticalCount = panel?.vertical?.points?.length ??
        panel?.vertical?.length ?? 0;
      return horizontalCount + verticalCount;
    });
    const totalCount = perRoofCounts.reduce((sum, count) => sum + count, 0);
    return { totalCount, perRoofCounts };
  }

  if (Array.isArray(design) && design.length > 0) {
    const perRoofCounts = design.map((roof) => {
      if (Array.isArray(roof?.panels)) return roof.panels.length;
      const horizontal = toNumber(roof?.horizontal, 0);
      const vertical = toNumber(roof?.vertical, 0);
      return horizontal + vertical;
    });
    const totalCount = perRoofCounts.reduce((sum, count) => sum + count, 0);
    if (totalCount > 0) return { totalCount, perRoofCounts };
  }

  if (Array.isArray(designPanels) && designPanels.length > 0) {
    const perRoofCounts = designPanels.map((panel) => {
      const horizontalCount = panel?.horizontal?.points?.length ??
        panel?.horizontal?.length ?? 0;
      const verticalCount = panel?.vertical?.points?.length ??
        panel?.vertical?.length ?? 0;
      return horizontalCount + verticalCount;
    });
    const totalCount = perRoofCounts.reduce((sum, count) => sum + count, 0);
    return { totalCount, perRoofCounts };
  }

  return { totalCount: 0, perRoofCounts: [] };
};

export const mapSelectedPanelsPerRoof = (
  selectedPanelsPerRoof = {},
  panelIndexMap = null
) => {
  const mapped = {};
  const mapByRoof = Array.isArray(panelIndexMap) ? panelIndexMap : null;

  Object.keys(selectedPanelsPerRoof || {}).forEach((roofKey) => {
    const roofIdx = Number(roofKey);
    if (!Number.isFinite(roofIdx)) return;
    const raw = selectedPanelsPerRoof[roofKey];
    const list = Array.isArray(raw) ? raw : [];
    const mappedList = list
      .map((panelIdx) => {
        const idx = Number(panelIdx);
        if (!Number.isFinite(idx)) return null;
        if (mapByRoof && Array.isArray(mapByRoof[roofIdx])) {
          const actualIdx = mapByRoof[roofIdx][idx];
          const mappedIdx = Number(actualIdx);
          return Number.isFinite(mappedIdx) ? mappedIdx : null;
        }
        return idx;
      })
      .filter((val) => Number.isFinite(val));
    mapped[roofIdx] = mappedList;
  });

  return mapped;
};

export const resolveRoofIndexFromPayload = (
  roofIdxRaw,
  roofIndexMap = null
) => {
  const roofIdx = Number(roofIdxRaw);
  if (!Number.isFinite(roofIdx)) return null;

  if (Array.isArray(roofIndexMap)) {
    const mappedIdx = Number(roofIndexMap[roofIdx]);
    if (Number.isFinite(mappedIdx)) return mappedIdx;
  }

  return roofIdx;
};

export const mapRoofKeyedPayloadToDesign = (
  payload = {},
  roofIndexMap = null
) => {
  if (!payload || typeof payload !== 'object') return {};

  const mapped = Array.isArray(payload) ? [] : {};
  Object.keys(payload).forEach((roofKey) => {
    const designRoofIdx = resolveRoofIndexFromPayload(roofKey, roofIndexMap);
    if (!Number.isFinite(designRoofIdx)) return;
    mapped[designRoofIdx] = payload[roofKey];
  });

  return mapped;
};

export const mapSelectedPanelsPerRoofToDesign = (
  selectedPanelsPerRoof = {},
  roofIndexMap = null,
  panelIndexMap = null
) => {
  const mappedByPanel = mapSelectedPanelsPerRoof(
    selectedPanelsPerRoof,
    panelIndexMap
  );

  return Object.keys(mappedByPanel).reduce((acc, roofKey) => {
    const designRoofIdx = resolveRoofIndexFromPayload(roofKey, roofIndexMap);
    if (!Number.isFinite(designRoofIdx)) return acc;
    acc[designRoofIdx] = mappedByPanel[roofKey];
    return acc;
  }, {});
};

export const mapStringingDataToDesign = (
  strings = null,
  roofIndexMap = null,
  panelIndexMap = null
) => {
  if (!Array.isArray(strings)) return strings;

  return strings.map((entry) => {
    const panels = Array.isArray(entry?.panels) ? entry.panels : [];
    const mappedPanels = panels
      .map((panel) => {
        if (!Array.isArray(panel) || panel.length < 2) return null;

        const payloadRoofIdx = Number(panel[0]);
        const payloadPanelIdx = Number(panel[1]);
        if (
          !Number.isFinite(payloadRoofIdx) ||
          !Number.isFinite(payloadPanelIdx)
        ) {
          return null;
        }

        const designRoofIdx = resolveRoofIndexFromPayload(
          payloadRoofIdx,
          roofIndexMap
        );
        if (!Number.isFinite(designRoofIdx)) return null;

        const roofPanelIndexMap = Array.isArray(panelIndexMap)
          ? panelIndexMap[payloadRoofIdx]
          : null;
        const actualPanelIdx = Array.isArray(roofPanelIndexMap)
          ? Number(roofPanelIndexMap[payloadPanelIdx])
          : payloadPanelIdx;
        if (!Number.isFinite(actualPanelIdx)) return null;

        return [designRoofIdx, actualPanelIdx];
      })
      .filter(Boolean);

    return {
      ...entry,
      panels: mappedPanels,
    };
  });
};

export const getSelectedPanelsPerRoofFromDesign = (
  design = [],
  selectedPanels = {}
) => {
  const mapped = {};
  if (!Array.isArray(design)) return mapped;

  design.forEach((roof, idx) => {
    const raw = selectedPanels?.[roof?.id] ?? [];
    const list = Array.isArray(raw) ? raw : [];
    const horizontalCount = toPanelCount(roof?.horizontal);
    const verticalCount = toPanelCount(roof?.vertical);
    const total = horizontalCount + verticalCount;
    const looksBinary = list.length === total && list.every(isBinaryEntry);
    const persistedIndices = looksBinary
      ? list
          .map((value, panelIdx) => (value ? panelIdx : null))
          .filter(Number.isFinite)
      : list
          .map((value) => Number(value))
          .filter((panelIdx) => isValidPanelIndex(panelIdx, total));
    mapped[idx] = persistedIndices.map((panelIdx) =>
      panelIdx < verticalCount
        ? horizontalCount + panelIdx
        : panelIdx - verticalCount
    );
  });

  return mapped;
};

export const buildSelectedPanelsStateFromCanvasSelection = (
  design = [],
  selectedPanelsPerRoof = {},
  previousSelectedPanels = {}
) => {
  const selectedPanels = { ...(previousSelectedPanels || {}) };

  (Array.isArray(design) ? design : []).forEach((roof, roofIdx) => {
    if (!roof?.id) return;
    const horizontalCount = toPanelCount(roof.horizontal);
    const verticalCount = toPanelCount(roof.vertical);
    const total = horizontalCount + verticalCount;
    const selectedRaw =
      selectedPanelsPerRoof?.[roofIdx] ??
      selectedPanelsPerRoof?.[String(roofIdx)] ??
      [];
    const selectedCanvasIndices = new Set(
      (Array.isArray(selectedRaw) ? selectedRaw : [])
        .map(Number)
        .filter((panelIdx) => isValidPanelIndex(panelIdx, total))
    );
    const persisted = Array(total).fill(0);

    selectedCanvasIndices.forEach((panelIdx) => {
      const persistedIdx =
        panelIdx < horizontalCount
          ? verticalCount + panelIdx
          : panelIdx - horizontalCount;
      if (persistedIdx >= 0 && persistedIdx < total) {
        persisted[persistedIdx] = 1;
      }
    });

    selectedPanels[roof.id] = persisted;
  });

  return selectedPanels;
};

export const buildProductionCallbackPayload = ({
  annualUsage,
  annualKwh,
  monthlyKwh,
  hourlyProduction,
  systemSizeKw,
  numTotalPanels,
  selectedPanelCount,
  perRoofCounts,
  segments,
  selectedPanelsPerRoof,
  productionPerPanel,
  productionPerString,
  pitchAndAzimuthPerRoof,
  shadingPerRoof,
  strings,
  invertersUsed,
  monthlyClippingKwh,
  clippingKwh,
  dcAcRatio,
  panelCount,
  mpptCountTotal,
  perRoofAnnualKwh,
  perRoofMonthlyAc,
  perRoofMonthlyPoa,
  perRoofClippingKwh,
  limitReached,
  limit_reached = false,
} = {}) => {
  const annualUsageValue = sumValues(annualUsage);
  const annualKwhValue = toNumber(annualKwh, 0);
  const monthlyValues = normalizeMonthlyValues(monthlyKwh, 0);
  const offset =
    annualUsageValue > 0 ? (annualKwhValue / annualUsageValue) * 100 : 0;

  const resolvedSelectedPanelsPerRoof =
    selectedPanelsPerRoof && typeof selectedPanelsPerRoof === 'object'
      ? selectedPanelsPerRoof
      : {};
  const resolvedSelectedPanelCount = Number.isFinite(Number(selectedPanelCount))
    ? Number(selectedPanelCount)
    : countSelectedPanels(resolvedSelectedPanelsPerRoof);
  const resolvedNumTotalPanels = Number.isFinite(Number(numTotalPanels))
    ? Number(numTotalPanels)
    : resolvedSelectedPanelCount;
  const resolvedSystemSizeKw = Number.isFinite(Number(systemSizeKw))
    ? Number(systemSizeKw)
    : null;
  const resolvedHourlyProduction =
    hourlyProduction !== undefined && hourlyProduction !== null
      ? hourlyProduction
      : [];
  const hasExplicitProductionPerPanel =
    Array.isArray(productionPerPanel) && productionPerPanel.length > 0;
  const resolvedProductionPerPanel = hasExplicitProductionPerPanel
    ? productionPerPanel
    : buildFallbackProductionPerPanel({
        segments,
        perRoofCounts,
        selectedPanelsPerRoof: resolvedSelectedPanelsPerRoof,
        selectedPanelCount: resolvedSelectedPanelCount,
        annualKwhValue,
      });
  const resolvedProductionPerString = Array.isArray(productionPerString)
    ? productionPerString
    : [];

  const payload = {
    offset: Math.round(offset),
    systemSize:
      resolvedSystemSizeKw !== null
        ? Number(resolvedSystemSizeKw).toFixed(3)
        : '0.000',
    yearlyProduction: annualKwhValue.toFixed(0),
    monthlyProduction: monthlyValues.map((value) => Number(value).toFixed(0)),
    hourlyProduction: resolvedHourlyProduction,
    numTotalPanels: resolvedNumTotalPanels,
    numSelectedPanels: resolvedSelectedPanelCount,
    productionPerPanel: resolvedProductionPerPanel,
    productionPerString: resolvedProductionPerString,
    pitchAndAzimuthPerRoof:
      pitchAndAzimuthPerRoof ?? buildPitchAndAzimuthPerRoof(segments),
    shadingPerRoof: shadingPerRoof ?? buildShadingPerRoof(segments),
    stringingData: strings || null,
    invertersUsed: invertersUsed ?? null,
    stringsCount: Array.isArray(strings) ? strings.length : 0,
    monthlyClippingKwh: normalizeMonthlyValues(monthlyClippingKwh, 0),
    clippingKwh: toNumber(clippingKwh, 0),
    dcAcRatio: dcAcRatio ?? null,
    panelCount: Number.isFinite(Number(panelCount))
      ? Number(panelCount)
      : resolvedSelectedPanelCount,
    selectedPanelsPerRoof: resolvedSelectedPanelsPerRoof,
    systemSizeKw: resolvedSystemSizeKw,
    mpptCountTotal: mpptCountTotal ?? null,
    perRoofAnnualKwh: perRoofAnnualKwh || {},
    perRoofMonthlyAc: perRoofMonthlyAc || {},
    perRoofMonthlyPoa: perRoofMonthlyPoa || {},
    perRoofClippingKwh: perRoofClippingKwh || {},
    limit_reached: Boolean(limitReached ?? limit_reached),
  };

  return payload;
};
